import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

import { useApp } from '../../context/AppContext';
import KPICard from '../../components/KPICard/KPICard';
import Card from '../../components/Card/Card';
import RankingList from '../../components/RankingList/RankingList';
import OutlierList from '../../components/OutlierList/OutlierList';
import HeatmapChart from '../../components/HeatmapChart/HeatmapChart';
import { StabilityBadge } from '../../components/Badge/Badge';
import '../../styles/layout.css';
import '../../styles/tables.css';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend
);

const BAR_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 9.5 }, color: '#8faabb' },
    },
    y: {
      grid: { color: '#f0f3f8' },
      ticks: { font: { size: 10 }, color: '#8faabb' },
    },
  },
};

const LINE_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      position: 'bottom',
      labels: { font: { size: 10 }, boxWidth: 9, padding: 8 },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 10 }, color: '#8faabb' },
    },
    y: {
      grid: { color: '#f0f3f8' },
      ticks: { font: { size: 10 }, color: '#8faabb' },
    },
  },
};

function filterUtil(D, op) {
  if (!op || op === 'All') return D.util || [];
  return (D.util || []).filter((r) => r.op === op);
}

export default function Utilisation() {
  const { operator, D } = useApp();
  const [dateRange, setDateRange] = useState('90d');
  const [metric, setMetric] = useState('hours');
  const [variantFilter, setVariantFilter] = useState('');
  const [search, setSearch] = useState('');

  const filteredUtil = useMemo(() => filterUtil(D, operator), [D, operator]);

  // Unique variants — util uses the field `v`
  const variants = useMemo(() => {
    const s = new Set(filteredUtil.map((r) => r.v || ''));
    return Array.from(s).filter(Boolean).sort();
  }, [filteredUtil]);

  // Per-aircraft aggregates
  const acAggs = useMemo(() => {
    const map = {};
    filteredUtil.forEach((r) => {
      const key = r.reg;
      if (!key) return;
      if (!map[key]) {
        map[key] = {
          R: key,
          V: r.v || '—',       // util field is `v` not `variant`
          O: r.op || '—',
          hrsSum: 0,
          cycSum: 0,
          count: 0,
        };
      }
      map[key].hrsSum += r.hrs || 0;
      map[key].cycSum += r.cyc || 0;
      map[key].count += 1;
    });
    return Object.values(map).map((a) => ({
      ...a,
      avgHrs: a.count ? parseFloat((a.hrsSum / a.count).toFixed(1)) : 0,
      avgCyc: a.count ? parseFloat((a.cycSum / a.count).toFixed(1)) : 0,
      totalHrs: Math.round(a.hrsSum),
      totalCyc: Math.round(a.cycSum),
    }));
  }, [filteredUtil]);

  const totalHrs   = useMemo(() => filteredUtil.reduce((s, r) => s + (r.hrs || 0), 0), [filteredUtil]);
  const totalCyc   = useMemo(() => filteredUtil.reduce((s, r) => s + (r.cyc || 0), 0), [filteredUtil]);

  const avgIntensity = useMemo(() => {
    if (!acAggs.length) return 0;
    return parseFloat((acAggs.reduce((s, a) => s + a.avgHrs, 0) / acAggs.length).toFixed(1));
  }, [acAggs]);

  const unstableCount = useMemo(() => acAggs.filter((a) => a.avgHrs < 5).length, [acAggs]);

  const ranked = useMemo(
    () => [...acAggs].sort((a, b) => b.avgHrs - a.avgHrs).slice(0, 12),
    [acAggs]
  );

  const outliers = useMemo(() => {
    const fleetAvg = avgIntensity;
    return acAggs
      .map((a) => ({ ...a, delta: parseFloat((a.avgHrs - fleetAvg).toFixed(1)) }))
      .filter((a) => Math.abs(a.delta) > 1.5)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, 5);
  }, [acAggs, avgIntensity]);

  // Fleet trend line: group by date
  const trendData = useMemo(() => {
    const allDates = [...new Set(filteredUtil.map((r) => r.date))].sort();
    let dates = allDates;
    if (dateRange === '7d')  dates = allDates.slice(-7);
    else if (dateRange === '30d') dates = allDates.slice(-30);

    const labels   = [];
    const hrsData  = [];
    const cycData  = [];

    dates.forEach((d) => {
      const rows = filteredUtil.filter((r) => r.date === d);
      labels.push(d.slice(5));
      hrsData.push(rows.length ? parseFloat((rows.reduce((s, r) => s + (r.hrs || 0), 0) / rows.length).toFixed(1)) : 0);
      cycData.push(rows.length ? parseFloat((rows.reduce((s, r) => s + (r.cyc || 0), 0) / rows.length).toFixed(1)) : 0);
    });

    return {
      labels,
      datasets: [
        { label: 'Avg Flight Hours', data: hrsData, borderColor: '#1246d6', backgroundColor: 'rgba(18,70,214,0.08)', tension: 0.35, fill: true, pointRadius: 2 },
        { label: 'Avg Cycles',       data: cycData, borderColor: '#f97316', backgroundColor: 'rgba(249,115,22,0.08)',  tension: 0.35, fill: true, pointRadius: 2 },
      ],
    };
  }, [filteredUtil, dateRange]);

  // Daily utilisation bar: last 30 days, optional variant filter (uses `r.v`)
  const dailyData = useMemo(() => {
    const src = variantFilter
      ? filteredUtil.filter((r) => r.v === variantFilter)
      : filteredUtil;
    const dates  = [...new Set(src.map((r) => r.date))].sort().slice(-30);
    const labels = [];
    const vals   = [];
    const field  = metric === 'hours' ? 'hrs' : 'cyc';

    dates.forEach((d) => {
      const rows = src.filter((r) => r.date === d);
      labels.push(d.slice(5));
      vals.push(rows.length ? parseFloat((rows.reduce((s, r) => s + (r[field] || 0), 0) / rows.length).toFixed(1)) : 0);
    });

    return {
      labels,
      datasets: [{
        data: vals,
        backgroundColor: metric === 'hours' ? 'rgba(18,70,214,0.7)' : 'rgba(249,115,22,0.7)',
        borderRadius: 3,
      }],
    };
  }, [filteredUtil, metric, variantFilter]);

  // Detail table rows
  const tableRows = useMemo(() => {
    const q = search.toLowerCase();
    return acAggs
      .map((a) => {
        const fleetRow   = D.fleet.find((f) => f.R === a.R) || {};
        const hrsPerCyc  = a.totalCyc ? (a.totalHrs / a.totalCyc).toFixed(2) : '—';
        const maxUtil    = 14;
        const pct        = Math.min(100, Math.round((a.avgHrs / maxUtil) * 100));
        const barCol     = a.avgHrs >= 8 ? '#16a34a' : a.avgHrs >= 5 ? '#d97706' : '#dc2626';
        const age        = fleetRow.Age;
        const intensityPct = age ? Math.min(100, Math.round((a.totalHrs / (age * 365 * 24)) * 100)) : 0;
        return { ...a, hrsPerCyc, pct, barCol, age, intensityPct };
      })
      .filter((r) => {
        if (!q) return true;
        return (r.R || '').toLowerCase().includes(q) ||
               (r.O || '').toLowerCase().includes(q) ||
               (r.V || '').toLowerCase().includes(q);
      })
      .sort((a, b) => b.avgHrs - a.avgHrs);
  }, [D, acAggs, search]);

  const fmtHrs = (h) => (h >= 1000 ? `${(h / 1000).toFixed(1)}k` : h.toFixed(0));

  return (
    <div className="page-content">

      {/* KPI Row */}
      <div className="krow krow-4">
        <KPICard label="Total Flight Hrs"  value={fmtHrs(totalHrs)}           sub="All aircraft combined"   delta="+5.2%" deltaDir="up" accent="kt" />
        <KPICard label="Total Cycles"      value={totalCyc.toLocaleString()}   sub="Across filtered fleet"  delta="+3.8%" deltaDir="up" accent="ko" />
        <KPICard label="Avg Intensity"     value={`${avgIntensity}h`}          sub="Daily hrs per aircraft"  accent="kg" />
        <KPICard label="Unstable Aircraft" value={unstableCount}               sub="Avg daily hrs < 5"       accent="kr" alert={unstableCount > 0} />
      </div>

      {/* Row 1: Ranking + Trend + Outliers */}
      <div className="g32">
        <Card title="Aircraft Utilisation Ranking" subtitle="Intensity by avg daily hrs" icon="🏆" iconClass="ib">
          <RankingList items={ranked} />
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card
            title="Fleet Utilisation Trend"
            icon="📈"
            iconClass="it"
            headerRight={
              <div className="cc">
                {['7d', '30d', '90d'].map((r) => (
                  <button key={r} className={`cbtn${dateRange === r ? ' on' : ''}`} onClick={() => setDateRange(r)}>
                    {r.toUpperCase()}
                  </button>
                ))}
              </div>
            }
          >
            <div className="cw">
              <Line data={trendData} options={LINE_OPTS} />
            </div>
          </Card>

          <Card title="Utilisation Outliers" subtitle="Deviating from fleet average" icon="⚠️" iconClass="ir">
            <OutlierList items={outliers} />
          </Card>
        </div>
      </div>

      {/* Row 2: Daily bar + Heatmap */}
      <div className="g2">
        <Card
          title="Daily Utilisation"
          icon="📊"
          iconClass="ib"
          headerRight={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div className="cc">
                {['hours', 'cycles'].map((m) => (
                  <button key={m} className={`cbtn${metric === m ? ' on' : ''}`} onClick={() => setMetric(m)}>
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </button>
                ))}
              </div>
              <select
                className="fsel"
                value={variantFilter}
                onChange={(e) => setVariantFilter(e.target.value)}
                style={{ minWidth: 110, height: 24, fontSize: 10.5 }}
              >
                <option value="">All Variants</option>
                {variants.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          }
        >
          <div className="cw">
            <Bar data={dailyData} options={BAR_OPTS} />
          </div>
        </Card>

        <Card title="Utilisation Heatmap" subtitle="Avg daily hrs – last 13 weeks" icon="🗓️" iconClass="it">
          <HeatmapChart utilData={filteredUtil} />
        </Card>
      </div>

      {/* Detail table */}
      <Card
        title="Aircraft Utilisation Detail"
        subtitle="All aircraft with utilisation metrics"
        icon="📋"
        iconClass="ib"
        noPad
        headerRight={
          <div className="sw">
            <span className="si">🔍</span>
            <input
              className="sinp"
              placeholder="Search aircraft…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      >
        <div className="ts full">
          <table>
            <thead>
              <tr>
                <th>Reg</th>
                <th>Operator</th>
                <th>Variant</th>
                <th>Total Hours</th>
                <th>Total Cycles</th>
                <th>Daily Util</th>
                <th>Intensity %</th>
                <th>Age</th>
                <th>Hrs / Cycle</th>
                <th>Stability</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', color: 'var(--tx3)', padding: '24px 14px' }}>
                    No aircraft match the current filters.
                  </td>
                </tr>
              ) : (
                tableRows.map((r) => (
                  <tr key={r.R}>
                    <td><strong>{r.R}</strong></td>
                    <td>{r.O}</td>
                    <td>{r.V}</td>
                    <td style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{r.totalHrs.toLocaleString()}</td>
                    <td style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{r.totalCyc.toLocaleString()}</td>
                    <td style={{ minWidth: 120 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx)', marginBottom: 3 }}>
                        {r.avgHrs.toFixed(1)}h/day
                      </div>
                      <div className="pb">
                        <div className="pbf" style={{ width: `${r.pct}%`, background: r.barCol }} />
                      </div>
                    </td>
                    <td>{r.intensityPct}%</td>
                    <td>{r.age ? `${r.age} yrs` : '—'}</td>
                    <td style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{r.hrsPerCyc}</td>
                    <td><StabilityBadge avgHrs={r.avgHrs} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

    </div>
  );
}
